import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  BriefcaseBusiness,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  MapPin,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  Share2,
  Star,
} from "lucide-react";

import { useAuth } from "../../../contexts/AuthContext";
import { getAccessToken } from "../../../awsAuth";
import ProfileActions from "../ProfileActions";
import ProfilePosts from "../ProfilePosts";
import FAQ from "./modules/FAQ";
import BeforeAfter from "./modules/BeforeAfter";
import BookingPublic from "./modules/BookingPublic";
import BusinessListings from "./modules/BusinessListings";
import "./BusinessProfile.scss";
import { photoVariant } from "../../../utils/imageUrl";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

  const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

const BUSINESS_PROFILE_MEMORY = new Map();
const BUSINESS_PROFILE_TTL = 5 * 60 * 1000;

const DAY_ORDER = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];

const DEFAULT_ACTION_BUTTONS = {
  call: true,
  message: true,
  booking: true,
  share: true,
};

const ACTIVE_BOOKING_STATUSES = [
  "pending",
  "confirmed",
  "reschedule_requested",
  "reschedule_proposed",
];

function isActiveBookingStatus(status) {
  return ACTIVE_BOOKING_STATUSES.includes(
    String(status || "")
  );
}

function getServiceBookingMeta(booking) {
  const status = String(booking?.status || "");

  if (status === "confirmed") {
    return {
      label: "Записано",
      className: "is-confirmed",
    };
  }

  if (status === "pending") {
    return {
      label: "В очікуванні",
      className: "is-pending",
    };
  }

  if (status === "reschedule_requested") {
    return {
      label: "Перенесення",
      className: "is-reschedule-requested",
    };
  }

  if (status === "reschedule_proposed") {
    return {
      label: "Новий час",
      className: "is-reschedule-proposed",
    };
  }

  return {
    label: "Запис",
    className: "is-default",
  };
}

const MODULE_META = {
  products: {
    title: "Товари",
    subtitle: "Асортимент бізнесу",
  },

  services: {
    title: "Послуги",
    subtitle: "Ціни, тривалість і запис",
  },

  listings: {
    title: "Оголошення",
    subtitle: "Публікації та пропозиції бізнесу",
  },

  vacancies: {
    title: "Вакансії",
    subtitle: "Актуальні відкриті позиції",
  },

  faq: {
    title: "FAQ",
    subtitle: "Поширені питання",
  },

   booking: {
    title: "Онлайн-запис",
    subtitle: "Запис до спеціаліста",
  },
};

const QUICK_NAV_META = {
  products: {
    label: "Товари",
    icon: "🛍",
  },

  services: {
    label: "Послуги",
    icon: "🧾",
  },

  listings: {
  label: "Оголошення",
  icon: "📢",
},

  posts: {
    label: "Пости",
    icon: "🖼",
  },

  vacancies: {
      label: "Вакансії",
      icon: "💼",
    },

  booking: {
    label: "Запис",
    icon: "📅",
  },

  faq: {
    label: "FAQ",
    icon: "❓",
  },

  beforeAfter: {
    label: "До / після",
    icon: "✨",
  },
};

const DEFAULT_QUICK_NAV_ORDER = [
  "services",
  "products",
  "listings",
  "posts",
  "booking",
  "vacancies",
  "faq",
  "beforeAfter",
];

function timeAgo(ts) {
  if (!ts) return "";

  const now = Date.now();
  const diffMs = Math.max(0, now - Number(ts));
  const sec = Math.floor(diffMs / 1000);

  if (sec < 60) return `${sec} с тому`;

  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} хв тому`;

  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs} год тому`;

  const days = Math.floor(hrs / 24);
  if (days === 1) return "вчора";
  if (days < 7) return `${days} дн тому`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} тиж тому`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} міс тому`;

  const years = Math.floor(months / 12);
  return `${years} р тому`;
}

function safeObj(v) {
  return v && typeof v === "object" ? v : {};
}

async function getJson(res) {
  return res.json().catch(() => ({}));
}

function normDayStr(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[–—−]/g, "-")
    .trim();
}

const DAY_ALIASES = {
  0: ["нд", "неділя", "sun", "sunday"],
  1: ["пн", "понеділок", "mon", "monday"],
  2: ["вт", "вівторок", "tue", "tuesday"],
  3: ["ср", "середа", "wed", "wednesday"],
  4: ["чт", "четвер", "thu", "thursday"],
  5: ["пт", "пʼятниця", "п'ятниця", "fri", "friday"],
  6: ["сб", "субота", "sat", "saturday"],
};

function parseTimeToMin(t) {
  const m = String(t || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;

  const hh = Number(m[1]);
  const mm = Number(m[2]);

  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;

  return hh * 60 + mm;
}

function dayIndexByAlias(token) {
  const v = normDayStr(token);

  for (const idx of Object.keys(DAY_ALIASES)) {
    if (DAY_ALIASES[idx].some((a) => v === a)) return Number(idx);
  }

  return null;
}

function expandDaysFromText(dayText) {
  const d = normDayStr(dayText);
  if (!d) return [];
  if (/(closed|зачинено|вихідн|не працю)/i.test(d)) return [];

  const parts = d.split("-");

  if (parts.length === 2) {
    const a = dayIndexByAlias(parts[0].trim());
    const b = dayIndexByAlias(parts[1].trim());

    if (a !== null && b !== null) {
      const out = [];
      let cur = a;

      while (true) {
        out.push(cur);
        if (cur === b) break;
        cur = (cur + 1) % 7;
        if (out.length > 7) break;
      }

      return out;
    }
  }

  const direct = dayIndexByAlias(d);
  if (direct !== null) return [direct];

  for (let i = 0; i < 7; i++) {
    if (DAY_ALIASES[i].some((a) => d.includes(a))) return [i];
  }

  return [];
}

function getWorkStatus(schedule) {
  const now = new Date();
  const dow = now.getDay();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  const rows = (Array.isArray(schedule) ? schedule : [])
    .map((x) => ({
      days: expandDaysFromText(x?.day),
      fromMin: parseTimeToMin(x?.from),
      toMin: parseTimeToMin(x?.to),
      to: x?.to,
    }))
    .filter((r) => r.days.length > 0);

  const row = rows.find((r) => r.days.includes(dow)) || null;

  if (!row || row.fromMin === null || row.toMin === null) {
    return { kind: "closed", label: "Закрито зараз" };
  }

  if (nowMin < row.fromMin || nowMin >= row.toMin) {
    return { kind: "closed", label: "Закрито зараз" };
  }

  const minsLeft = row.toMin - nowMin;

  if (minsLeft <= 30) {
    return { kind: "soon", label: `Скоро закривається · до ${row.to}` };
  }

  return { kind: "open", label: `Відкрито зараз · до ${row.to}` };
}

function makeScheduleRows(schedule) {
  const arr = Array.isArray(schedule) ? schedule : [];
  const byDay = new Map();

  arr.forEach((row) => {
    const day = String(row?.day || "").trim();
    if (!day) return;

    byDay.set(day, {
      day,
      from: row?.from || "",
      to: row?.to || "",
    });
  });

  return DAY_ORDER.map((day) => {
    const found = byDay.get(day);

    if (!found) {
      return {
        day,
        time: "Вихідний",
        off: true,
      };
    }

    return {
      day,
      time: `${found.from} — ${found.to}`,
      off: false,
    };
  });
}

function hydrateBusinessState(data, setters) {
  setters.setProfile(data?.profile || null);
  setters.setAccount(data?.account || null);
  setters.setFollowersCount(Number(data?.followersCount || 0));
  setters.setFollowingCount(Number(data?.followingCount || 0));
  setters.setListingsCount(Number(data?.listingsCount || 0));
  setters.setPostsCount(Number(data?.postsCount || 0));
  setters.setTrustRating(Number(data?.trustRating || 0));
  setters.setTrustTier(data?.trustTier || "");
  setters.setProducts(Array.isArray(data?.products) ? data.products : []);
  setters.setServices(Array.isArray(data?.services) ? data.services : []);
  setters.setVacancies(Array.isArray(data?.vacancies) ? data.vacancies : []);
  setters.setListings(Array.isArray(data?.listings) ? data.listings : []);
  setters.setPosts(Array.isArray(data?.posts) ? data.posts : []);
}

function updateBusinessCache(cacheKey, patch) {
  if (!cacheKey) return;

  const current = BUSINESS_PROFILE_MEMORY.get(cacheKey) || {};

  BUSINESS_PROFILE_MEMORY.set(cacheKey, {
    ...current,
    ...patch,
    savedAt: Date.now(),
  });
}

function RatingStars({ value = 0 }) {
  const normalized = Math.max(0, Math.min(10, Number(value || 0)));
  const activeStars = Math.round(normalized / 2);

  return (
    <div className="bp-stars" aria-label={`Рейтинг ${normalized}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= activeStars ? "is-on" : ""}>
          <Star size={13} strokeWidth={2.25} />
        </span>
      ))}
    </div>
  );
}

function ProductsBlock({ items = [], onOrder, ownerId }) {
  const navigate = useNavigate();

  const openAll = () => {
    if (ownerId) {
      navigate(`/profile/${encodeURIComponent(ownerId)}/products`);
      return;
    }

    navigate("/business/products");
  };

  return (
    <section className="bp-widget-section">
      <div className="bp-widget-head">
        <div>
          <h2>{MODULE_META.products.title}</h2>
          <p>{MODULE_META.products.subtitle}</p>
        </div>

        {items.length > 0 && (
          <button type="button" onClick={openAll}>
            Переглянути всі
          </button>
        )}
      </div>

      {!items.length ? (
        <div className="bp-empty-module">
          <strong>Товарів поки немає</strong>
          <span>Власник бізнесу ще не додав асортимент.</span>
        </div>
      ) : (
        <div className="bp-products-rail">
          {items.slice(0, 8).map((item) => {
            const photo = item.mainPhoto || item.photos?.[0] || "";
            const price =
              Number(item.price || 0) > 0
                ? `${item.price} ${item.currency || "ГРН"}`
                : "Ціну уточнюйте";

            const note =
              item.availability === "preorder"
                ? "Під замовлення"
                : item.availability === "out_of_stock"
                ? "Немає в наявності"
                : "В наявності";

            return (
              <article
                key={item.listingId || item.id}
                className="bp-product-card"
                onClick={() => navigate(`/listing/${item.listingId || item.id}`)}
              >
                <div className="bp-product-card__image">
                  {photo ? (
                    <>
                      <img
                        src={photoVariant(photo, "sm")}
                        alt=""
                        className="bp-product-card__image-bg"
                        loading="lazy"
                        decoding="async"
                      />

                      <img
                        src={photoVariant(photo, "sm")}
                        alt=""
                        className="bp-product-card__image-main"
                        loading="lazy"
                        decoding="async"
                      />
                    </>
                  ) : (
                    <span className="bp-product-card__placeholder">
                      {String(item.title || "T")
                        .slice(0, 1)
                        .toUpperCase()}
                    </span>
                  )}

                  <div className="bp-product-card__image-gradient" />

                  <span
                    className={[
                      "bp-product-card__availability",
                      item.availability === "out_of_stock"
                        ? "is-out"
                        : item.availability === "preorder"
                        ? "is-preorder"
                        : "is-available",
                    ].join(" ")}
                  >
                    {note}
                  </span>
                </div>

                <div className="bp-product-card__body">
                  <strong>{item.title || "Товар"}</strong>

                  <div className="bp-product-card__bottom">
                    <b>{price}</b>

                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onOrder(item);
                      }}
                    >
                      Замовити
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ServicesBlock({
  items = [],
  onBookService,
  ownerId,
  clientBookings = [],
}) {
  const navigate = useNavigate();

  const openAll = () => {
    if (ownerId) {
      navigate(
        `/profile/${encodeURIComponent(ownerId)}/services`
      );
      return;
    }

    navigate("/business/services");
  };

  const formatPrice = (item) => {
    if (Number(item.price || 0) <= 0) {
      return "Ціну уточнюйте";
    }

    return `${item.price} ${item.currency || "ГРН"}`;
  };

  const activeBookingsByService = useMemo(() => {
    const map = new Map();

    const activeBookings = Array.isArray(clientBookings)
      ? clientBookings.filter((booking) =>
          isActiveBookingStatus(booking?.status)
        )
      : [];

    for (const booking of activeBookings) {
      const serviceId = String(booking?.serviceId || "");

      if (!serviceId) continue;

      if (!map.has(serviceId)) {
        map.set(serviceId, booking);
      }
    }

    return map;
  }, [clientBookings]);

  return (
    <section className="bp-widget-section bp-services-widget">
      <div className="bp-widget-head">
        <div>
          <h2>{MODULE_META.services.title}</h2>
          <p>{MODULE_META.services.subtitle}</p>
        </div>

        {items.length > 0 && (
          <button type="button" onClick={openAll}>
            Переглянути всі
          </button>
        )}
      </div>

      {!items.length ? (
        <div className="bp-empty-module">
          <strong>Послуг поки немає</strong>

          <span>
            Власник бізнесу ще не додав послуги.
          </span>
        </div>
      ) : (
        <div className="bp-services-rail">
          {items.slice(0, 6).map((item) => {
            const serviceId = String(
              item.listingId || item.id || ""
            );

            const photo =
              item.mainPhoto ||
              item.photos?.[0] ||
              "";

            const serviceBooking =
              activeBookingsByService.get(serviceId) || null;

            const bookingMeta =
              getServiceBookingMeta(serviceBooking);

            const duration =
              item.duration || "Тривалість не вказана";

            return (
              <article
                key={serviceId}
                className={[
                  "bp-service-card",
                  serviceBooking ? "has-booking" : "",
                  serviceBooking?.status
                    ? `has-${serviceBooking.status}`
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() =>
                  navigate(`/listing/${serviceId}`)
                }
              >
                <div className="bp-service-card__image">
                  {photo ? (
                    <>
                      <img
                        src={photoVariant(photo, "sm")}
                        alt=""
                        className="bp-service-card__image-bg"
                        loading="lazy"
                        decoding="async"
                      />

                      <img
                        src={photoVariant(photo, "sm")}
                        alt=""
                        className="bp-service-card__image-main"
                        loading="lazy"
                        decoding="async"
                      />
                    </>
                  ) : (
                    <span className="bp-service-card__placeholder">
                      {String(item.title || "S")
                        .slice(0, 1)
                        .toUpperCase()}
                    </span>
                  )}

                  <div className="bp-service-card__overlay" />

                  <span className="bp-service-card__duration">
                    <Clock3 size={13} strokeWidth={2.25} />
                    {duration}
                  </span>
                </div>

                <div className="bp-service-card__body">
                  <div className="bp-service-card__info">
                    <strong>
                      {item.title || "Послуга"}
                    </strong>

                    {item.description && (
                      <p>{item.description}</p>
                    )}
                  </div>

                  <div className="bp-service-card__bottom">
                    <b>{formatPrice(item)}</b>

                    <button
                      type="button"
                      className={[
                        "bp-service-book-button",
                        bookingMeta.className,
                      ].join(" ")}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onBookService(item);
                      }}
                    >
                      {bookingMeta.label}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function VacanciesBlock({ items = [], ownerId }) {
  const navigate = useNavigate();

  if (!items.length) return null;

  const openAll = () => {
    if (ownerId) {
      navigate(
        `/profile/${encodeURIComponent(ownerId)}/vacancies`
      );
      return;
    }

    navigate("/business/vacancies");
  };

  const formatSalary = (item) => {
    const from = Number(
      item.salaryFrom || item.price || 0
    );

    const to = Number(item.salaryTo || 0);
    const currency = item.currency || "ГРН";

    if (from > 0 && to > 0) {
      return `${from}–${to} ${currency}`;
    }

    if (from > 0) {
      return `від ${from} ${currency}`;
    }

    return "За домовленістю";
  };

  const formatMeta = (item) => {
    const parts = [
      item.workFormat,
      item.employmentType,
      item.location,
    ].filter(Boolean);

    return parts.length
      ? parts.join(" · ")
      : "Деталі роботи не вказані";
  };

  return (
    <section className="bp-widget-section bp-vacancies-widget">
      <div className="bp-widget-head">
        <div>
          <h2>{MODULE_META.vacancies.title}</h2>
          <p>{MODULE_META.vacancies.subtitle}</p>
        </div>

        <button type="button" onClick={openAll}>
          Переглянути всі
        </button>
      </div>

      <div className="bp-vacancies-rail">
        {items.slice(0, 6).map((item) => {
          const vacancyId =
            item.listingId || item.id;

          const photo =
            item.mainPhoto ||
            item.photos?.[0] ||
            "";

          return (
            <article
              key={vacancyId}
              className="bp-vacancy-card"
              onClick={() =>
                navigate(`/listing/${vacancyId}`)
              }
            >
              <div className="bp-vacancy-card__visual">
                {photo ? (
                  <>
                    <img
                      src={photoVariant(photo, "sm")}
                      alt=""
                      className="bp-vacancy-card__image-bg"
                      loading="lazy"
                      decoding="async"
                    />

                    <img
                      src={photoVariant(photo, "sm")}
                      alt=""
                      className="bp-vacancy-card__image-main"
                      loading="lazy"
                      decoding="async"
                    />
                  </>
                ) : (
                  <div className="bp-vacancy-card__placeholder">
                    <BriefcaseBusiness
                      size={28}
                      strokeWidth={2.1}
                    />
                  </div>
                )}

                <div className="bp-vacancy-card__gradient" />

                <span className="bp-vacancy-card__badge">
                  Вакансія
                </span>

                <div className="bp-vacancy-card__content">
                  <strong>
                    {item.title || "Нова вакансія"}
                  </strong>

                  <p>{formatMeta(item)}</p>

                  <div className="bp-vacancy-card__bottom">
                    <b>{formatSalary(item)}</b>

                    <span aria-hidden="true">
                      <ChevronRight
                        size={18}
                        strokeWidth={2.4}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default function BusinessProfile() {
  const navigate = useNavigate();
  const params = useParams();
  const { userId } = useAuth();

  const viewedUserId = params.userId || params.id || userId;
  const cacheKey = viewedUserId ? `business-profile:${viewedUserId}` : null;

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState(null);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [listingsCount, setListingsCount] = useState(0);
  const [postsCount, setPostsCount] = useState(0);
  const [trustRating, setTrustRating] = useState(0);
  const [trustTier, setTrustTier] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [isAvatarOpen, setIsAvatarOpen] = useState(false);
  const [isAvatarClosing, setIsAvatarClosing] = useState(false);
  const [timeTick, setTimeTick] = useState(Date.now());
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [listings, setListings] = useState([]);
  const [posts, setPosts] = useState([]);
  const [bookingService, setBookingService] = useState(null);
  const [bookingFlowKey, setBookingFlowKey] = useState(0);

  const [clientBookings, setClientBookings] =
  useState([]);

  const effectiveUserId = account?.id || profile?.userId || viewedUserId;

  const isOwner =
    !!effectiveUserId &&
    !!userId &&
    String(effectiveUserId) === String(userId);

  useEffect(() => {
    const t = setInterval(() => setTimeTick(Date.now()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let alive = true;

    async function loadBusinessProfile() {
      if (!viewedUserId) {
        setLoading(false);
        return;
      }

      const cached = cacheKey ? BUSINESS_PROFILE_MEMORY.get(cacheKey) : null;
      const hasFreshCache =
        cached && Date.now() - Number(cached.savedAt || 0) < BUSINESS_PROFILE_TTL;

      if (hasFreshCache) {
        hydrateBusinessState(cached, {
          setProfile,
          setAccount,
          setFollowersCount,
          setFollowingCount,
          setListingsCount,
          setPostsCount,
          setTrustRating,
          setTrustTier,
          setProducts,
          setServices,
          setVacancies,
          setListings,
          setPosts,
        });

        setLoading(false);
        setError("");
      } else {
        setLoading(true);
      }

      try {
        setError("");

        const token = await getAccessToken().catch(() => null);
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        if (token && !isOwner) {
        fetch(
          `${API_URL}/api/business/booking/requests/me?role=client`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
          .then((response) =>
            response.json().catch(() => ({}))
          )
          .then((bookingData) => {
            if (!alive) return;

            setClientBookings(
              Array.isArray(bookingData?.items)
                ? bookingData.items
                : []
            );
          })
          .catch(() => {
            if (!alive) return;
            setClientBookings([]);
          });
      }

        const ownProfile = !!userId && String(viewedUserId) === String(userId);

        const businessUrl = ownProfile
          ? `${API_URL}/api/business/profile/me`
          : `${API_URL}/api/business/profile/${encodeURIComponent(viewedUserId)}`;

        const businessRes = await fetch(businessUrl, { headers });
        const businessData = await getJson(businessRes);

        if (!businessRes.ok) {
          throw new Error(
            businessData?.error || "Не вдалося завантажити бізнес-профіль"
          );
        }

        const nextProfile = businessData?.profile || null;
        const nextUserBase = businessData?.userBase || null;
        const targetId = nextUserBase?.id || nextProfile?.userId || viewedUserId;

        if (!alive) return;

        setProfile(nextProfile);
        setAccount(nextUserBase);

        updateBusinessCache(cacheKey, {
          profile: nextProfile,
          account: nextUserBase,
        });

        setLoading(false);

        if (!nextUserBase && targetId) {
          fetch(`${API_URL}/api/account/${encodeURIComponent(targetId)}`)
            .then((r) => r.json())
            .then((data) => {
              if (!alive) return;

              setAccount(data || null);

              updateBusinessCache(cacheKey, {
                account: data || null,
              });
            })
            .catch(() => {});
        }

        fetch(
          `${API_URL}/api/follow/stats?target=${encodeURIComponent(targetId)}`
        )
          .then((r) => r.json())
          .then((data) => {
            if (!alive) return;

            const nextFollowers = Number(data?.followers || 0);
            const nextFollowing = Number(data?.following || 0);

            setFollowersCount(nextFollowers);
            setFollowingCount(nextFollowing);

            updateBusinessCache(cacheKey, {
              followersCount: nextFollowers,
              followingCount: nextFollowing,
            });
          })
          .catch(() => {});

                fetch(`${API_URL}/api/listings/user/${encodeURIComponent(targetId)}`)
          .then((r) => r.json())
          .then((data) => {
            if (!alive) return;

            const allListings = Array.isArray(data) ? data : [];

            const normalListings = allListings.filter((item) => {
              const category = String(item.category || "").toLowerCase();
              const moduleKey = String(
                item.businessModule || item.moduleKey || item.entityType || ""
              ).toLowerCase();

              return (
                category !== "карʼєра" &&
                category !== "кар'єра" &&
                moduleKey !== "vacancies" &&
                moduleKey !== "business_vacancy"
              );
            });

            setListings(normalListings);
            setListingsCount(normalListings.length);

            updateBusinessCache(cacheKey, {
              listings: normalListings,
              listingsCount: normalListings.length,
            });
          })
          .catch(() => {});

          fetch(
              `${API_URL}/api/business/products/user/${encodeURIComponent(targetId)}`
            )
          .then((r) => r.json())
          .then((data) => {
            if (!alive) return;

            const nextProducts = Array.isArray(data?.items) ? data.items : [];

            setProducts(nextProducts);

            updateBusinessCache(cacheKey, {
              products: nextProducts,
            });
          })
          .catch(() => {});

        fetch(
          `${API_URL}/api/business/services/user/${encodeURIComponent(targetId)}`
        )
          .then((r) => r.json())
          .then((data) => {
            if (!alive) return;

            const nextServices = Array.isArray(data?.items) ? data.items : [];

            setServices(nextServices);

            updateBusinessCache(cacheKey, {
              services: nextServices,
            });
          })
          .catch(() => {});

        fetch(
          `${API_URL}/api/business/vacancies/user/${encodeURIComponent(targetId)}`
        )
          .then((r) => r.json())
          .then((data) => {
            if (!alive) return;

            const nextVacancies = Array.isArray(data?.items) ? data.items : [];

            setVacancies(nextVacancies);

            updateBusinessCache(cacheKey, {
              vacancies: nextVacancies,
            });
          })
          .catch(() => {});

        fetch(`${API_URL}/api/posts/user/${encodeURIComponent(targetId)}`, {
          headers,
        })
          .then((response) => response.json())
          .then((data) => {
            if (!alive) return;

            const nextPosts = Array.isArray(data)
              ? data
              : Array.isArray(data?.items)
              ? data.items
              : [];

            setPosts(nextPosts);
            setPostsCount(nextPosts.length);

            updateBusinessCache(cacheKey, {
              posts: nextPosts,
              postsCount: nextPosts.length,
            });
          })
          .catch(() => {
            if (!alive) return;

            setPosts([]);
            setPostsCount(0);
          });

        fetch(
          `${API_URL}/api/trust/${encodeURIComponent(targetId)}/details`
        )
          .then((r) => r.json())
          .then((data) => {
            if (!alive) return;

            const rating = Number(
              data?.trustLevel ||
                (data?.trustScore ? Number(data.trustScore) / 10 : 0)
            );

            const nextRating = Number.isFinite(rating) ? rating : 0;
            const nextTier = data?.trustTier || "";

            setTrustRating(nextRating);
            setTrustTier(nextTier);

            updateBusinessCache(cacheKey, {
              trustRating: nextRating,
              trustTier: nextTier,
            });
          })
          .catch(() => {});
      } catch (e) {
        console.error("[BusinessProfile] load error:", e);

        if (alive) {
          if (!hasFreshCache) {
            setError(e.message || "Помилка завантаження бізнес-профілю");
          }

          setLoading(false);
        }
      }
    }

    loadBusinessProfile();

    return () => {
      alive = false;
    };
  }, [viewedUserId, userId, cacheKey]);

  const avatar = account?.avatar || profile?.hero?.image || DEFAULT_AVATAR;

  const openAvatar = () => {
  setIsAvatarClosing(false);
  setIsAvatarOpen(true);
};

const closeAvatar = () => {
  if (isAvatarClosing) return;

  setIsAvatarClosing(true);

  window.setTimeout(() => {
    setIsAvatarOpen(false);
    setIsAvatarClosing(false);
  }, 300);
};

useEffect(() => {
  if (!isAvatarOpen) return undefined;

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      closeAvatar();
    }
  };

  window.addEventListener("keydown", handleKeyDown);

  return () => {
    window.removeEventListener("keydown", handleKeyDown);
  };
}, [isAvatarOpen, isAvatarClosing]);

  const name =
    account?.username ||
    profile?.title ||
    profile?.hero?.title ||
    "Бізнес";

  const bio =
    account?.bio ||
    profile?.subtitle ||
    profile?.hero?.subtitle ||
    "Бізнес-профіль Cvanto";

  const location = account?.location || "";
  const phone = account?.phone || "";

  const businessDescription = String(
  profile?.description ||
    profile?.about?.text ||
    ""
  ).trim();

  // SEO (Phase A, 2026-09): this page had zero title/description/canonical/
  // JSON-LD despite being the profile URL for every business account
  // (specialists, masters, service providers) — exactly the entity type the
  // SEO project needs to rank for long-tail "<profession> <city>" queries.
  // Mirrors Profile.js's (individual-account) Helmet block + cvanto-ssr's
  // lib/profileSeo.js business branch, so SPA and bot-rendered SSR agree.
  // `profile.seo.title`/`profile.seo.description` (already part of the
  // BusinessProfiles schema — server/routes/business/profile.js — but never
  // consumed anywhere until now) win when the owner set them explicitly.
  const businessOrigin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://cvanto.com";
  const businessCanonicalUrl = `${businessOrigin}/profile/${encodeURIComponent(viewedUserId || "")}`;

  const professionSignal =
    (Array.isArray(account?.serviceCategories) && account.serviceCategories[0]) ||
    (Array.isArray(account?.profileCategories) && account.profileCategories[0]) ||
    "";

  const businessSeoTitle =
    profile?.seo?.title ||
    (professionSignal && location
      ? `${professionSignal} — ${location} — ${name} | Cvanto`
      : professionSignal
        ? `${professionSignal} — ${name} | Cvanto`
        : location
          ? `${name} — ${location} | Cvanto`
          : `${name} | Cvanto`);

  const businessSeoDesc =
    profile?.seo?.description ||
    (businessDescription || bio || `Бізнес-профіль ${name} на Cvanto`)
      .trim()
      .replace(/\s+/g, " ")
      .slice(0, 160);

  const businessJsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: {
      "@type": "Organization",
      name,
      url: businessCanonicalUrl,
      image: avatar || DEFAULT_AVATAR,
      description: businessSeoDesc || undefined,
      ...(location ? { address: { "@type": "PostalAddress", addressLocality: location } } : {}),
    },
  };

  const schedule = useMemo(
    () => (Array.isArray(profile?.schedule) ? profile.schedule : []),
    [profile?.schedule]
  );

  const workStatus = useMemo(() => {
    void timeTick;
    return getWorkStatus(schedule);
  }, [schedule, timeTick]);

  const scheduleRows = useMemo(() => makeScheduleRows(schedule), [schedule]);

  const modules = useMemo(() => {
    const fallbackOrder = [
      "services",
      "products",
      "listings",
      "posts",
      "vacancies",
    ];
    const order = Array.isArray(profile?.modulesOrder)
      ? profile.modulesOrder
      : fallbackOrder;

    const enabledModules = safeObj(profile?.enabledModules);

    return order.filter((key) => enabledModules[key] !== false);
  }, [profile]);

  const hasProducts = modules.includes("products");
  const hasServices = modules.includes("services");
  const hasVacancies = modules.includes("vacancies");
  const hasListings = modules.includes("listings");
  const hasFaq = modules.includes("faq");
  const hasBeforeAfter = modules.includes("beforeAfter");
  const hasBooking = modules.includes("booking");

  const quickNavEnabled =
  profile?.quickNavEnabled === true;

const quickNavItems = useMemo(() => {
  if (!quickNavEnabled) {
    return [];
  }

  const selectedQuickNavKeys = new Set(
    Array.isArray(profile?.quickNavOrder)
      ? profile.quickNavOrder
      : DEFAULT_QUICK_NAV_ORDER
  );

  return modules
    .filter((key) =>
      selectedQuickNavKeys.has(key)
    )
    .filter((key) => QUICK_NAV_META[key])
    .filter((key) => {
      if (key === "vacancies") {
        return vacancies.length > 0;
      }

      if (key === "posts") {
        return posts.length > 0;
      }

      return true;
    })
    .map((key) => ({
      key,
      ...QUICK_NAV_META[key],
    }));
}, [
  profile?.quickNavOrder,
  quickNavEnabled,
  modules,
  vacancies.length,
  posts.length,
]);
  
  const actionButtons = {
  ...DEFAULT_ACTION_BUTTONS,
  ...safeObj(profile?.actionButtons),
  };

  const showCallButton =
    actionButtons.call !== false &&
    Boolean(String(phone || "").trim());

  const showMessageButton =
    actionButtons.message !== false;

  const showBookingButton =
    actionButtons.booking !== false &&
    hasServices &&
    hasBooking &&
    services.length > 0;

  const showShareButton =
    actionButtons.share !== false;

  const guestActionsCount = [
    showCallButton,
    showMessageButton,
    showBookingButton,
    showShareButton,
  ].filter(Boolean).length;

  const scrollToBusinessModule =
  useCallback((moduleKey) => {
    const element = document.getElementById(
      `bp-module-${moduleKey}`
    );

    if (!element) return;

    const topOffset = 82;

    const elementTop =
      element.getBoundingClientRect().top +
      window.scrollY -
      topOffset;

    window.scrollTo({
      top: Math.max(0, elementTop),
      behavior: "smooth",
    });
  }, []);

    const toggleSchedule = useCallback((e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      setScheduleOpen((v) => !v);
    }, []);

    const onCall = useCallback(() => {
      const tel = String(phone || "").replace(/[^\d+]/g, "");
      if (!tel) return;
      window.location.href = `tel:${tel}`;
    }, [phone]);

    const onChat = useCallback(() => {
      if (!effectiveUserId) return;
      navigate(`/messages?to=${encodeURIComponent(effectiveUserId)}`);
    }, [navigate, effectiveUserId]);

    const onBook = useCallback(() => {
  if (!effectiveUserId || !services.length) return;

  setBookingService(null);
  setBookingFlowKey((current) => current + 1);

  window.setTimeout(() => {
    document
      .getElementById("bp-booking-public")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  }, 80);
}, [effectiveUserId, services.length]);

  const onShare = useCallback(async () => {
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: name,
          text: bio,
          url,
        });
        return;
      } catch {}
    }

    try {
      await navigator.clipboard.writeText(url);
    } catch {}
  }, [name, bio]);

  const onOrderProduct = useCallback(
    (item) => {
      if (!effectiveUserId) return;

      navigate(
        `/messages?to=${encodeURIComponent(
          effectiveUserId
        )}&prefill=${encodeURIComponent(
          `Вітаю! Хочу замовити товар: ${item.title}. Підкажіть, будь ласка, умови.`
        )}`
      );
    },
    [navigate, effectiveUserId]
  );

  const onBookService = useCallback((item) => {
  setBookingService(item || null);
  setBookingFlowKey((current) => current + 1);

  window.setTimeout(() => {
    document
      .getElementById("bp-booking-public")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  }, 80);
}, []);

  const onMenuClick = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (isOwner) {
        navigate("/settings");
        return;
      }

      setShowActions(true);
    },
    [isOwner, navigate]
  );

  const handleBlockedChange = useCallback(() => {}, []);

  if (loading) {
    return (
      <main className="business-profile">
        <div className="bp-loading">Завантаження...</div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="business-profile">
        <div className="bp-error">{error}</div>
      </main>
    );
  }

  return (
    <main className="business-profile">
      <Helmet>
        <title>{businessSeoTitle}</title>
        <meta name="description" content={businessSeoDesc} />
        <link rel="canonical" href={businessCanonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cvanto" />
        <meta property="og:title" content={businessSeoTitle} />
        <meta property="og:description" content={businessSeoDesc} />
        <meta property="og:url" content={businessCanonicalUrl} />
        <meta property="og:image" content={avatar || DEFAULT_AVATAR} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={businessSeoTitle} />
        <meta name="twitter:description" content={businessSeoDesc} />
        <meta name="twitter:image" content={avatar || DEFAULT_AVATAR} />

        <script type="application/ld+json">{JSON.stringify(businessJsonLd)}</script>
      </Helmet>

      <section className="bp-profile-shell">
        <div className="bp-topbar">
          <button
            type="button"
            className="bp-nav-btn"
            onClick={() => navigate(-1)}
            aria-label="Назад"
          >
            <ChevronLeft size={25} strokeWidth={2.35} />
          </button>

          <button
            type="button"
            className="bp-nav-btn"
            aria-label={isOwner ? "Налаштування" : "Меню"}
            onClick={onMenuClick}
          >
            <MoreHorizontal size={24} strokeWidth={2.45} />
          </button>
        </div>

                <section className="bp-hero">
          <div className="bp-aura" aria-hidden="true">
            <img
              src={avatar || DEFAULT_AVATAR}
              alt=""
              className="bp-aura__image"
              onLoad={(e) => {
                e.currentTarget.classList.add("is-loaded");
              }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_AVATAR;
              }}
            />
          </div>

          <div className="bp-head">
            <div className="bp-avatar-wrap">
              <button
                type="button"
                className="bp-avatar-button"
                onClick={openAvatar}
                aria-label="Відкрити фото профілю"
              >
                <img
                  src={avatar}
                  alt={name || "Фото профілю"}
                  className="bp-avatar"
                  loading="eager"
                  decoding="async"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = DEFAULT_AVATAR;
                  }}
                />
              </button>

              <span className="bp-business-badge">
                Бізнес
              </span>
            </div>

            <div className="bp-main-info">
              <h1>{name}</h1>
              <p>{bio}</p>

              <div className="bp-meta-line">
                <span className="bp-rating-inline">
                  <RatingStars value={trustRating} />
                  <strong>{trustRating > 0 ? trustRating.toFixed(1) : "—"}</strong>
                </span>

                <span className="bp-location-inline">
                  <MapPin size={14} strokeWidth={2.35} />
                  <em>{location || "Локація не вказана"}</em>
                </span>
              </div>
            </div>
          </div>

          {isOwner ? (
            <div
              className="bp-actions bp-actions--owner"
              style={{
                gridTemplateColumns:
                  "repeat(2, minmax(0, 1fr))",
              }}
            >
              <button
                type="button"
                onClick={() => navigate("/add-ad")}
              >
                <Plus size={21} strokeWidth={2.35} />
                <em>Створити оголошення</em>
              </button>

              <button
                type="button"
                onClick={() => navigate("/business/edit")}
              >
                <Pencil size={20} strokeWidth={2.25} />
                <em>Налаштувати бізнес-простір</em>
              </button>
            </div>
          ) : guestActionsCount > 0 ? (
            <div
              className="bp-actions bp-actions--guest"
              style={{
                gridTemplateColumns: `repeat(${guestActionsCount}, minmax(0, 1fr))`,
              }}
            >
              {showCallButton && (
                <button type="button" onClick={onCall}>
                  <Phone size={20} strokeWidth={2.25} />
                  <em>Подзвонити</em>
                </button>
              )}

              {showMessageButton && (
                <button type="button" onClick={onChat}>
                  <MessageCircle size={20} strokeWidth={2.25} />
                  <em>Написати</em>
                </button>
              )}

              {showBookingButton && (
                <button type="button" onClick={onBook}>
                  <CalendarDays size={20} strokeWidth={2.25} />
                  <em>Записатись</em>
                </button>
              )}

              {showShareButton && (
                <button type="button" onClick={onShare}>
                  <Share2 size={20} strokeWidth={2.25} />
                  <em>Поділитись</em>
                </button>
              )}
            </div>
          ) : null}

          <div className="bp-stats">
            <button
              type="button"
              className="bp-stat"
              onClick={() =>
                navigate(
                  `/profile/${encodeURIComponent(effectiveUserId)}?follow=followers`
                )
              }
            >
              <strong>{followersCount}</strong>
              <span>Підписники</span>
            </button>

            <button
              type="button"
              className="bp-stat"
              onClick={() =>
                navigate(
                  `/profile/${encodeURIComponent(effectiveUserId)}?follow=following`
                )
              }
            >
              <strong>{followingCount}</strong>
              <span>Підписки</span>
            </button>

            <button
              type="button"
              className="bp-stat"
              onClick={() => scrollToBusinessModule("listings")}
            >
              <strong>{listingsCount}</strong>
              <span>Оголошення</span>
            </button>

            <button
              type="button"
              className="bp-stat"
              onClick={() => {
                document
                  .getElementById("bp-module-posts")
                  ?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
              }}
            >
              <strong>{postsCount}</strong>
              <span>Пости</span>
            </button>
          </div>

          {businessDescription && (
            <div className="bp-about-text">
              {businessDescription}
            </div>
          )}

          <div className="bp-schedule-card">
            <button
              type="button"
              className={`bp-schedule-toggle bp-schedule-toggle--${workStatus.kind}`}
              onClick={toggleSchedule}
            >
              <span className="bp-schedule-left">
                <span className="bp-schedule-dot" />
                <span>
                  <strong>{workStatus.label}</strong>
                  <em>Графік роботи</em>
                </span>
              </span>

              <ChevronDown
                className={`bp-schedule-chevron ${scheduleOpen ? "is-open" : ""}`}
                size={20}
                strokeWidth={2.4}
              />
            </button>

            <div className={`bp-schedule-wrap ${scheduleOpen ? "is-open" : ""}`}>
              <div className="bp-schedule-inner">
                {scheduleRows.length > 0 ? (
                  <div className="bp-schedule-list">
                    {scheduleRows.map((row) => (
                      <div
                        key={row.day}
                        className={`bp-schedule-line ${row.off ? "is-off" : ""}`}
                      >
                        <span>{row.day}</span>
                        <strong>{row.time}</strong>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bp-schedule-empty">Графік не заповнено</div>
                )}
              </div>
            </div>
          </div>
        </section>

        {quickNavItems.length > 0 && (
          <nav
            className="bp-quick-nav"
            aria-label="Швидка навігація профілем"
          >
            <div className="bp-quick-nav__track">
              {quickNavItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="bp-quick-nav__item"
                  onClick={() =>
                    scrollToBusinessModule(item.key)
                  }
                >
                  <span className="bp-quick-nav__icon">
                    {item.icon}
                  </span>

                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </nav>
        )}

        {modules.length > 0 ? (
  modules.map((moduleKey) => {
    if (moduleKey === "services") {
      return (
        <div
          key={moduleKey}
          id="bp-module-services"
          className="bp-module-anchor"
        >
          <ServicesBlock
            items={services}
            onBookService={onBookService}
            ownerId={effectiveUserId}
            clientBookings={clientBookings}
          />
        </div>
      );
    }

    if (moduleKey === "products") {
      return (
        <div
          key={moduleKey}
          id="bp-module-products"
          className="bp-module-anchor"
        >
          <ProductsBlock
            items={products}
            onOrder={onOrderProduct}
            ownerId={effectiveUserId}
          />
        </div>
      );
    }

    if (moduleKey === "posts") {
  return (
    <div
      key={moduleKey}
      id="bp-module-posts"
      className="bp-module-anchor"
    >
      <section className="bp-widget-section bp-posts-section">
        <div className="bp-widget-head">
          <div>
            <h2>Пости</h2>
            <p>Новини та публікації бізнесу</p>
          </div>

          {posts.length > 1 && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/profile/${encodeURIComponent(
                    effectiveUserId
                  )}/posts`
                )
              }
            >
              Переглянути всі
            </button>
          )}
        </div>

        <ProfilePosts
          posts={
            Array.isArray(posts)
              ? posts.slice(0, 1)
              : posts
          }
          userMeta={{
            userName: name,
            avatar,
            effectiveUserId,
          }}
          onPatchPosts={(updater) => {
            setPosts((previous) => {
              const next =
                typeof updater === "function"
                  ? updater(previous)
                  : updater;

              const safeNext = Array.isArray(next)
                ? next
                : [];

              setPostsCount(safeNext.length);

              updateBusinessCache(cacheKey, {
                posts: safeNext,
                postsCount: safeNext.length,
              });

              return safeNext;
            });
          }}
          currentUserId={userId}
          apiBase={API_URL}
          timeAgo={timeAgo}
          userId={effectiveUserId}
        />
      </section>
    </div>
  );
}

    if (moduleKey === "listings") {
      return (
        <div
          key={moduleKey}
          id="bp-module-listings"
          className="bp-module-anchor"
        >
          <section className="bp-widget-section">
            <div className="bp-widget-head">
              <div>
                <h2>
                  {MODULE_META.listings.title}
                </h2>

                <p>
                  {MODULE_META.listings.subtitle}
                </p>
              </div>
            </div>

            <BusinessListings
              listings={listings}
              ownerId={effectiveUserId}
              onOpenListing={(id) =>
                navigate(`/listing/${id}`)
              }
            />
          </section>
        </div>
      );
    }

    if (moduleKey === "vacancies") {
      return (
        <div
          key={moduleKey}
          id="bp-module-vacancies"
          className="bp-module-anchor"
        >
          <VacanciesBlock
            items={vacancies}
            ownerId={effectiveUserId}
          />
        </div>
      );
    }

    if (moduleKey === "faq") {
      return (
        <div
          key={moduleKey}
          id="bp-module-faq"
          className="bp-module-anchor"
        >
          <FAQ
            ownerId={effectiveUserId}
            readonly={true}
          />
        </div>
      );
    }

    if (moduleKey === "beforeAfter") {
      return (
        <div
          key={moduleKey}
          id="bp-module-beforeAfter"
          className="bp-module-anchor"
        >
          <BeforeAfter
            ownerId={effectiveUserId}
            readonly={true}
          />
        </div>
      );
    }

    if (moduleKey === "booking") {
      return (
        <div
          key={moduleKey}
          id="bp-module-booking"
          className="bp-module-anchor"
        >
          <section
            id="bp-booking-public"
            className="bp-widget-section"
          >
            {isOwner ? (
              <BookingPublic
                ownerId={effectiveUserId}
                services={services}
                ownerView={true}
              />
            ) : (
              <BookingPublic
                ownerId={effectiveUserId}
                services={services}
                initialService={bookingService}
              />
            )}
          </section>
        </div>
      );
    }

    return null;
  })
) : (
  <section className="bp-widget-section">
    <div className="bp-empty-module">
      <strong>Блоки ще не додані</strong>

      <span>
        Власник бізнесу ще не налаштував
        модулі бізнес-профілю.
      </span>
    </div>
  </section>
)}
</section>

{showActions && !isOwner && (
  <ProfileActions
    onClose={() => setShowActions(false)}
    userInfo={{
      id: effectiveUserId,
      username: name,
      avatar,
      createdAt: account?.createdAt || profile?.createdAt,
    }}
    onBlockedChange={handleBlockedChange}
  />
)}

{isAvatarOpen && (
  <div
    className={[
      "bp-avatar-modal",
      isAvatarClosing ? "is-closing" : "",
    ]
      .filter(Boolean)
      .join(" ")}
    onClick={closeAvatar}
    role="dialog"
    aria-modal="true"
    aria-label="Фото профілю"
  >
    <img
      src={avatar}
      alt={name || "Фото профілю"}
      className="bp-avatar-modal__image"
      onError={(event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = DEFAULT_AVATAR;
      }}
    />
  </div>
)}
</main>
);
}